import React, { useContext, useMemo, useState } from 'react';

interface DeviceSettingsContext {
  deviceApartmentId: string | null;
  deviceResidentId: string | null;
  deviceChosenResidentIds: string[];
  isDisabledBottomNavigation: boolean;
  setDeviceApartmentId: (apartmentId: string | null) => void;
  setDeviceResidentId: (residentId: string | null) => void;
  setDeviceChosenResidentIds: (residentIds: string[]) => void;
  setIsDisabledBottomNavigation: (isDisabled: boolean) => void;
}

const DeviceSettingsContext = React.createContext<DeviceSettingsContext | null>(null);

export const useDeviceSettings = () => {
  const context = useContext(DeviceSettingsContext);

  if (!context) {
    throw Error('No device settings context found!');
  }

  return context;
};

export const DeviceSettingsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [deviceApartmentId, setDeviceApartmentId] = useState<string | null>(null);
  const [deviceResidentId, setDeviceResidentId] = useState<string | null>(null);
  const [deviceChosenResidentIds, setDeviceChosenResidentIds] = useState<string[]>([]);
  const [isDisabledBottomNavigation, setIsDisabledBottomNavigation] = useState(false);

  const value = useMemo(
    () => ({
      deviceApartmentId,
      deviceResidentId,
      deviceChosenResidentIds,
      isDisabledBottomNavigation,
      setDeviceApartmentId,
      setDeviceResidentId,
      setDeviceChosenResidentIds,
      setIsDisabledBottomNavigation,
    }),
    [deviceApartmentId, deviceResidentId, deviceChosenResidentIds, isDisabledBottomNavigation],
  );

  return <DeviceSettingsContext.Provider value={value}>{children}</DeviceSettingsContext.Provider>;
};

// For class components that cannot use hooks. Must be removed when App.jsx is fully converted to functional components
export const withDeviceSettings = <P extends object>(Component: React.ComponentType<P>) => {
  const WithDeviceSettings = (props: Omit<P, 'deviceApartmentId' | 'deviceResidentId'>) => {
    const { deviceApartmentId, deviceResidentId } = useDeviceSettings();
    return <Component {...(props as P)} deviceApartmentId={deviceApartmentId} deviceResidentId={deviceResidentId} />;
  };
  return WithDeviceSettings;
};
